import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import 'react-quill/dist/quill.snow.css';
import { contentTextLooksRtl, prepareContentRichPreviewHtml } from '../../utils/contentRichPreviewHtml';

export interface ContentRichPreviewView {
  label: string;
  html: string;
}

export interface ContentRichPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Raw editor HTML from Quill (or HTML string from server); not Delta JSON for display. */
  html: string;
  title?: string;
  /** When multiple documents/sides are available, show a switcher in the header. */
  views?: ContentRichPreviewView[];
}

const ContentRichPreviewDialog: React.FC<ContentRichPreviewDialogProps> = ({
  open,
  onClose,
  html,
  title = 'Content preview',
  views,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [activeViewIndex, setActiveViewIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setFullScreen(false);
      setActiveViewIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveViewIndex(0);
  }, [open, views, html, title]);

  const resolvedViews = views?.length ? views : [{ label: title, html }];
  const clampedViewIndex = Math.min(activeViewIndex, Math.max(0, resolvedViews.length - 1));
  const activeHtml = resolvedViews[clampedViewIndex]?.html ?? html;
  const safeHtml = prepareContentRichPreviewHtml(activeHtml);
  const previewIsRtl = useMemo(() => contentTextLooksRtl(activeHtml), [activeHtml]);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      disableRestoreFocus
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column', height: '100%', m: 0, maxHeight: '100%' }
          : {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(92vh, 920px)',
              width: '100%',
              m: { xs: 0.5, sm: 1 },
            },
      }}
    >
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar
          variant="dense"
          sx={{
            gap: 0.75,
            minHeight: 48,
            px: { xs: 1, sm: 1.5 },
            flexWrap: 'wrap',
            rowGap: 0.5,
          }}
        >
          <Typography variant="subtitle1" component="div" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600 }} noWrap>
            {title}
          </Typography>
          {resolvedViews.length > 1 ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={clampedViewIndex}
              onChange={(_, next) => {
                if (typeof next === 'number') {
                  setActiveViewIndex(next);
                }
              }}
              aria-label="Preview side"
              sx={{ flexShrink: 0 }}
            >
              {resolvedViews.map((view, index) => (
                <ToggleButton key={`${view.label}-${index}`} value={index} aria-label={view.label}>
                  {view.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : null}
          <IconButton
            color="inherit"
            onClick={() => setFullScreen((v) => !v)}
            aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          p: { xs: 1, sm: 1.25 },
          bgcolor: 'background.default',
        }}
      >
        <Box
          className="ql-snow"
          sx={{
            maxWidth: '100%',
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: (theme) => theme.shadows[1],
            border: (theme) => `1px solid ${theme.palette.divider}`,
            '& .ql-container.ql-snow': { border: 'none' },
            '& .ql-editor': {
              minHeight: fullScreen ? 'calc(100dvh - 120px)' : '8rem',
              fontSize: '1rem',
              lineHeight: 1.6,
              ...(previewIsRtl
                ? { direction: 'rtl', textAlign: 'right !important' }
                : { direction: 'ltr' }),
              p: { xs: 1.25, sm: 1.75 },
            },
            ...(previewIsRtl
              ? {
                  '& .ql-editor p:not(.ql-align-center), & .ql-editor li:not(.ql-align-center), & .ql-editor .ql-align-left':
                    { textAlign: 'right !important' },
                }
              : {}),
            '& .ql-editor .ql-align-right': { textAlign: 'right' },
            '& .ql-editor .ql-align-center': { textAlign: 'center' },
            ...(!previewIsRtl ? { '& .ql-editor .ql-align-left': { textAlign: 'left' } } : {}),
            '& .ql-editor img, & .ql-editor video, & .ql-editor audio': {
              maxWidth: '100%',
              height: 'auto',
            },
          }}
        >
          <Box className="ql-container ql-snow" sx={{ border: 'none' }}>
            <Box
              component="div"
              className="ql-editor"
              dir={previewIsRtl ? 'rtl' : undefined}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ContentRichPreviewDialog;
